import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';

import Swal from 'sweetalert2';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@jem-open/jem-ui';
import apiResponseHandler from '../../../utils/apiResponseHandler';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';

import { environment } from '../../../environment/environment';
import { localStorageService } from '../../../services';

let cancelToken;
const createData = {
  override_date: null,
  run_date: null,
  subscription_batch_configuration: null,
  created_by: null,
};

/**
 * @summary AdvanceBatchConfigurationOverrideModal component
 *
 * @param {Props} props
 * @component
 */
const AdvanceBatchConfigurationOverrideModal = (props) => {
  const [state, setState] = useState({
    batchOverrides: [],
    advance_name: 'Mukuru subscription',
    company_name: 'Mukuru',
    addNewOverride: false,
    loading: true,
  });
  const {
    toggleConfigurationOverridesModal,
    activeBatchConfig,
    showOverrideModal,
  } = props;

  const getOverrides = () => {
    setState((prev) => ({ ...prev, loading: true }));
    const headers = {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    cancelToken = axios.CancelToken.source();
    axios
      .get(`${environment.baseUrl}/advance_batch_overrides/`, {
        headers,
        params: {
          advance_batch_configuration: activeBatchConfig.id,
        },
        cancelToken: cancelToken.token,
      })
      .then((res) => {
        setState((prev) => ({
          ...prev,
          batchOverrides: res.data,
          loading: false,
        }));
      })
      .catch(() => {
        setState((prev) => ({ ...prev, loading: false }));
      });
  };

  const setAddNewOverride = (enabled) => {
    setState((prev) => ({ ...prev, addNewOverride: enabled }));
  };

  const deleteOverride = (batchOverrideID) => {
    axios
      .delete(
        `${environment.baseUrl}/advance_batch_overrides/${batchOverrideID}/`
      )
      .then(() => {
        Swal.fire({
          title: 'Advance Override was deleted',
          icon: 'success',
        });
        getOverrides();
      })
      .catch((error) => {
        apiResponseHandler.handleApiError(
          'Advance Override was not deleted',
          error
        );
      });
  };

  const canOverrideBeDeleted = (batchOverride) => {
    const today = new Date();

    return (
      today < moment(batchOverride.run_date, 'YYYY-MM-DD') &&
      today < moment(batchOverride.override_date, 'YYYY-MM-DD')
    );
  };

  const createNewOverride = () => {
    const user = localStorageService.getUserOrThrow();
    const currentUserId = user.id;
    createData.created_by = currentUserId;
    createData.advance_batch_configuration = activeBatchConfig.id;

    const url = `${environment.baseUrl}/advance_batch_overrides/`;

    axios
      .post(url, createData)
      .then(() => {
        Swal.fire({
          title: 'Advance Batch Configuration successfully submitted.',
          icon: 'success',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 10000,
          timerProgressBar: true,
        });
        getOverrides();
        setAddNewOverride(false);
      })
      .catch((error) => {
        apiResponseHandler.handleApiError('Error', error);
      });
  };

  useEffect(() => {
    getOverrides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { key: 'id', label: 'ID', width: '80px' },
    { key: 'override_date', label: 'Override Date' },
    { key: 'run_date', label: 'Run Date' },
    {
      key: 'actions',
      label: 'Actions',
      width: '120px',
      render: (value, batchOverride) =>
        canOverrideBeDeleted(batchOverride) ? (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => deleteOverride(batchOverride.id)}>
            Delete
          </Button>
        ) : null,
    },
  ];

  const emptyState = (
    <EmptyState
      icon="fas fa-calendar-alt"
      message="This configuration has no overrides"
    />
  );

  return (
    <Dialog
      open={showOverrideModal}
      onOpenChange={(next) => {
        if (!next) toggleConfigurationOverridesModal();
      }}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {activeBatchConfig?.pay_cycle
              ? `Subscription Overrides for: ${activeBatchConfig.pay_cycle.name} (${activeBatchConfig.pay_cycle.employer.company_name})`
              : 'Subscription Overrides'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-md overflow-y-auto">
          <div className="flex justify-end">
            {!state.addNewOverride && (
              <Button variant="primary" onClick={() => setAddNewOverride(true)}>
                Create Override
              </Button>
            )}
          </div>

          {state.addNewOverride && (
            <div className="flex flex-wrap items-end gap-sm rounded-md border border-greyscale-border-subtle p-md">
              <div className="flex flex-col gap-xxs">
                <Label htmlFor="overrideDate">Override Date</Label>
                <Input
                  type="date"
                  name="overrideDate"
                  id="overrideDate"
                  placeholder="Override Date"
                  onChange={(e) => {
                    createData.override_date = e.target.value;
                  }}
                  required
                />
              </div>
              <div className="flex flex-col gap-xxs">
                <Label htmlFor="runDate">Run Date</Label>
                <Input
                  type="date"
                  name="runDate"
                  id="runDate"
                  placeholder="Run Date"
                  onChange={(e) => {
                    createData.run_date = e.target.value;
                  }}
                  required
                />
              </div>
              <div className="flex items-center gap-xs">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => createNewOverride()}>
                  Create
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setAddNewOverride(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="table-responsive">
            <DataTable
              columns={columns}
              data={Array.isArray(state.batchOverrides) ? state.batchOverrides : []}
              loading={state.loading}
              emptyState={emptyState}
              skeletonRows={5}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={toggleConfigurationOverridesModal}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

AdvanceBatchConfigurationOverrideModal.defaultProps = {
  activeBatchConfig: null,
};

AdvanceBatchConfigurationOverrideModal.propTypes = {
  toggleConfigurationOverridesModal: PropTypes.func.isRequired,
  activeBatchConfig: PropTypes.shape({
    id: PropTypes.string,
  }),
  showOverrideModal: PropTypes.bool.isRequired,
};

export default AdvanceBatchConfigurationOverrideModal;
